// Tier-3 click-through modal opened from a v3 list row's BurstBadge.
//
// Renders the related events for a (src_ip, intent_name, host, path)
// burst in the row's time window. Each row in the modal is itself a
// click target — clicking opens the v3 event drawer for that event.
//
// No-mock-data invariants:
//   * loading state shows a spinner, NOT a fake "0 events"
//   * error state shows the actual error, NOT silent empty list
//   * empty result (server returned [] in the window) shows "No
//     related events in this window" — the honest empty signal

import React, { useState, useEffect, useContext } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Typography,
  Box,
  Stack,
  CircularProgress,
  IconButton,
  Tooltip,
  Chip,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import ReactCountryFlag from "react-country-flag";
import { EventContext } from "../../../../../contexts/user/EventContext";
import { formatDate } from "../../../../../utils/format";
import colors from "../../../../../utils/colors";
import { actionLabel } from "../../../../../utils/wafEventV3";

function statusChip(httpStatus) {
  const s = Number(httpStatus);
  if (Number.isNaN(s)) return null;
  const cfg = s >= 500 ? { bg: "#FEE2E2", fg: "#B91C1C" }
            : s === 403 || s === 429 ? { bg: "#FFEDD5", fg: "#9A3412" }
            : s >= 400 ? { bg: "#FEF3C7", fg: "#A16207" }
            : { bg: "#DCFCE7", fg: "#1F7C2A" };
  return (
    <Chip size="small" label={String(s)} sx={{
      backgroundColor: cfg.bg, color: cfg.fg,
      fontSize: 11, height: 20, fontWeight: 600,
    }} />
  );
}

function actionChip(action) {
  if (!action) return null;
  const cfg = String(action).toLowerCase().includes("block") ? { bg: "#FEE2E2", fg: "#B91C1C" }
            : String(action).toLowerCase().includes("challenge") ? { bg: "#FFEDD5", fg: "#9A3412" }
            : { bg: colors.neutral[100], fg: colors.neutral[700] };
  return (
    <Chip size="small" label={actionLabel(action)} sx={{
      backgroundColor: cfg.bg, color: cfg.fg,
      fontSize: 11, height: 20, fontWeight: 500,
    }} />
  );
}

export default function BurstListModal({ open, onClose, correlation, tuple, onEventClick }) {
  // `correlation` carries group_id + related_event_count + first_seen + last_seen
  // `tuple` carries src_ip, intent_name, host, path — drilled from the v3 row
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const { getSmartParseEventGroup } = useContext(EventContext);

  useEffect(() => {
    if (!open || !correlation || !correlation.group_id || !tuple) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      setData(null);
      try {
        // Default window: correlation's own bounds (a touch beyond,
        // since CH ts is sub-second-precise and the row that produced
        // the badge isn't always inside [first_seen, last_seen]).
        const fromMs = correlation.first_seen
          ? new Date(correlation.first_seen).getTime() - 60 * 1000
          : Date.now() - 60 * 60 * 1000;
        const toMs = correlation.last_seen
          ? new Date(correlation.last_seen).getTime() + 60 * 1000
          : Date.now();
        const body = {
          src_ip:      tuple.src_ip,
          intent_name: tuple.intent_name,
          host:        tuple.host,
          path:        tuple.path,
          from:        new Date(fromMs).toISOString(),
          to:          new Date(toMs).toISOString(),
          count:       100,
          page:        1,
        };
        const res = await getSmartParseEventGroup(correlation.group_id, body);
        if (!cancelled) setData(res);
      } catch (e) {
        if (!cancelled) setError(e && (e.response && e.response.data && e.response.data.error || e.message) || "Failed to load related events");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, correlation, tuple, getSmartParseEventGroup]);

  const events = (data && data.events) || [];
  const total = data ? data.total : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pr: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Typography component="span" sx={{ fontWeight: 600 }}>Related events</Typography>
          {tuple ? (
            <Typography component="span" sx={{ fontSize: 12, color: colors.neutral[600] }}>
              {tuple.src_ip} · {tuple.intent_name} · {tuple.host}{tuple.path}
            </Typography>
          ) : null}
        </Stack>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        ) : error ? (
          <Box sx={{ p: 4 }}>
            <Typography sx={{ color: "#B91C1C", fontWeight: 500 }}>
              Could not load related events
            </Typography>
            <Typography sx={{ fontSize: 12, color: colors.neutral[600], mt: 0.5 }}>
              {String(error)}
            </Typography>
          </Box>
        ) : events.length === 0 ? (
          <Box sx={{ p: 4 }}>
            <Typography sx={{ color: colors.neutral[700] }}>
              No related events in this window.
            </Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${colors.neutral[200]}` }}>
              <Typography sx={{ fontSize: 12, color: colors.neutral[700] }}>
                Showing {events.length} of {total != null ? total : events.length} related events
              </Typography>
            </Box>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontSize: 12, color: colors.neutral[800] }}>Time</TableCell>
                  <TableCell sx={{ fontSize: 12, color: colors.neutral[800] }}>Method · URI</TableCell>
                  <TableCell sx={{ fontSize: 12, color: colors.neutral[800] }}>Source IP</TableCell>
                  <TableCell sx={{ fontSize: 12, color: colors.neutral[800] }}>Action</TableCell>
                  <TableCell sx={{ fontSize: 12, color: colors.neutral[800] }}>Status</TableCell>
                  <TableCell sx={{ fontSize: 12, color: colors.neutral[800] }}>Event ID</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {events.map((ev) => (
                  <TableRow
                    key={ev.event_id}
                    hover
                    onClick={() => onEventClick && onEventClick(ev)}
                    sx={{ cursor: onEventClick ? "pointer" : "default" }}
                  >
                    <TableCell sx={{ fontSize: 12 }}>{formatDate(ev.ts) || "—"}</TableCell>
                    <TableCell>
                      <Stack spacing={0.25}>
                        <Typography sx={{ fontSize: 12, fontWeight: 500, color: colors.neutral[1000] }}>
                          {ev.method}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: "#1E40AF" }}>
                          {ev.uri && ev.uri.length > 60 ? ev.uri.substr(0, 60) + "…" : ev.uri}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        {ev.country_iso ? (
                          <ReactCountryFlag countryCode={ev.country_iso} svg style={{ width: 18, height: 12 }} />
                        ) : null}
                        <Typography sx={{ fontSize: 12 }}>{ev.src_ip}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>{actionChip(ev.action_taken)}</TableCell>
                    <TableCell>{statusChip(ev.http_status)}</TableCell>
                    <TableCell>
                      {ev.event_id ? (
                        <Tooltip title={ev.event_id} arrow>
                          <Typography sx={{ fontSize: 12, fontFamily: "monospace", color: "#1E40AF" }}>
                            {ev.event_id.substring(0, 8)}…
                          </Typography>
                        </Tooltip>
                      ) : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
